import clsx from "clsx";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  ETH,
  Network,
  THOR,
  gasToken,
  networkLabel,
  validateAddress,
} from "rujira.js";
import {
  AccountProvider,
  Button,
  IconDenom,
  Icons,
  SwapSelect,
  SwapSelectOption,
  TokenSelect,
  useTranslation,
  Warning,
} from "rujira.ui";
import { useWalletAccounts } from "../../services/accounts";
import {
  BanxaBuyCapabilities,
  BanxaFiat,
  BanxaMinimumAmountError,
  BanxaPaymentMethod,
  BanxaQuote,
  banxaFeeTotal,
  createBanxaOrder,
  getBanxaBuyCapabilities,
  getBanxaQuote,
} from "../../services/banxa";
import { FIAT_DECIMALS, quantizeFiat } from "../../services/banxaFiat";
import { banxaNetwork } from "../../services/banxaNetwork";
import { BanxaFeeBreakdown } from "./BanxaFeeBreakdown";
import { BanxaReviewRow } from "./BanxaReviewRow";
type Product = {
  crypto: string;
  blockchain: string;
  network: Network;
  symbol: string;
};

// SwapSelect uses the app's standard 8-decimal amount representation.
const FIAT_SCALE = 10n ** BigInt(FIAT_DECIMALS);

const fiatSelectOption = (fiat: BanxaFiat): SwapSelectOption => ({
  asset: `fiat:${fiat.id}`,
  type: "FIAT",
  chain: ETH,
  metadata: { symbol: fiat.id, decimals: FIAT_DECIMALS },
});

const fiatsForPaymentMethod = (
  fiats: BanxaFiat[],
  paymentMethodId: BanxaPaymentMethod
) => {
  const hasPaymentMethodData = fiats.some(
    ({ supportedPaymentMethods }) => supportedPaymentMethods.length
  );
  return hasPaymentMethodData
    ? fiats.filter(({ supportedPaymentMethods }) =>
        supportedPaymentMethods.some(({ id }) => id === paymentMethodId)
      )
    : fiats;
};

const productAsset = (product: Product): SwapSelectOption => ({
  asset: `${product.blockchain}:${product.crypto}`,
  type: "NATIVE",
  chain: product.network,
  metadata: { symbol: product.symbol, decimals: 8 },
});

const buyProducts = (capabilities?: BanxaBuyCapabilities): Product[] =>
  capabilities?.cryptocurrencies.flatMap((cryptocurrency) =>
    cryptocurrency.blockchains.flatMap((blockchain) => {
      const network = banxaNetwork(blockchain.id);
      return network
        ? [
            {
              crypto: cryptocurrency.id,
              blockchain: blockchain.id,
              network,
              symbol: cryptocurrency.id,
            },
          ]
        : [];
    })
  ) || [];

const destinationPriority = ({ crypto, network }: Product) => {
  const symbol = crypto.trim().toUpperCase();
  if (symbol === "USDC" && network === ETH) return 0;
  if (symbol === "USDT" && network === ETH) return 1;
  if (symbol === "RUNE" && network === THOR) return 2;
  return 3;
};

const formatFiatAmount = (amount: bigint) => {
  const whole = amount / FIAT_SCALE;
  const fraction = (amount % FIAT_SCALE)
    .toString()
    .padStart(FIAT_DECIMALS, "0")
    .replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
};

const shortAddress = (address: string) =>
  `${address.slice(0, 8)}…${address.slice(-6)}`;

const formatNumber = (value: string, maximumFractionDigits = 8) => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? new Intl.NumberFormat(undefined, {
        maximumFractionDigits,
      }).format(parsed)
    : value;
};

const quoteRate = (quote: BanxaQuote) => {
  const fiat = Number(quote.fiatAmount);
  const crypto = Number(quote.cryptoAmount);
  if (!Number.isFinite(fiat) || !Number.isFinite(crypto) || crypto <= 0) {
    return undefined;
  }
  return fiat / crypto;
};

export const BanxaOnRamp: FC<{ onBack: () => void }> = ({ onBack }) => {
  const accountProvider = useWalletAccounts();
  return (
    <BanxaOnRampContent accountProvider={accountProvider} onBack={onBack} />
  );
};

export const BanxaOnRampContent: FC<{
  accountProvider: AccountProvider;
  onBack: () => void;
}> = ({ accountProvider, onBack }) => {
  const { t } = useTranslation("common");
  const [stage, setStage] = useState<"configure" | "review">("configure");
  const [fiat, setFiat] = useState("USD");
  const [fiatAmount, setFiatAmount] = useState(100n * FIAT_SCALE);
  const [paymentMethodId, setPaymentMethodId] =
    useState<BanxaPaymentMethod>("debit-credit-card");
  const [destinationId, setDestinationId] = useState("");
  const [destinationOpen, setDestinationOpen] = useState(false);
  const destinationSelectRef = useRef<HTMLDivElement>(null);
  const [quote, setQuote] = useState<BanxaQuote>();
  const [quoteState, setQuoteState] = useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");
  const [error, setError] = useState("");
  const [minimumError, setMinimumError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [capabilities, setCapabilities] = useState<BanxaBuyCapabilities>();
  const [capabilitiesState, setCapabilitiesState] = useState<
    "loading" | "ready" | "error"
  >("loading");

  const paymentMethods: {
    id: BanxaPaymentMethod;
    label: string;
    description: string;
    Icon: typeof Icons.CreditCard;
  }[] = [
    {
      id: "debit-credit-card",
      label: t("banxaPaymentCard"),
      description: t("banxaPaymentCardBody"),
      Icon: Icons.CreditCard,
    },
    {
      id: "sepa-bank-transfer",
      label: t("banxaPaymentSepa"),
      description: t("banxaPaymentSepaBody"),
      Icon: Icons.Building,
    },
  ];

  useEffect(() => {
    let active = true;
    getBanxaBuyCapabilities()
      .then((nextCapabilities) => {
        if (!active) return;
        setCapabilities(nextCapabilities);
        setCapabilitiesState("ready");
      })
      .catch(() => {
        if (!active) return;
        setCapabilitiesState("error");
      });

    return () => {
      active = false;
    };
  }, []);

  const destinations = useMemo(() => {
    const products = buyProducts(capabilities);
    const values = (accountProvider.accounts || []).flatMap(({ address }) =>
      products
        .filter(
          (product) =>
            address.networks.includes(product.network) &&
            validateAddress(product.network, address.address)
        )
        .map((product) => ({
          ...product,
          address,
          id: `${product.crypto}:${product.blockchain}:${address.address}`,
        }))
    );

    return values
      .filter(
        (destination, index) =>
          values.findIndex((candidate) => candidate.id === destination.id) ===
          index
      )
      .sort(
        (left, right) => destinationPriority(left) - destinationPriority(right)
      );
  }, [accountProvider.accounts, capabilities]);

  useEffect(() => {
    if (destinationId && destinations.some(({ id }) => id === destinationId)) {
      return;
    }
    const preferred =
      destinations.find(
        ({ crypto, network }) => crypto === "USDC" && network === ETH
      ) || destinations[0];
    setDestinationId(preferred?.id || "");
  }, [destinationId, destinations]);

  useEffect(() => {
    if (!destinationOpen) return;

    const closeDestinationSelect = (event: MouseEvent) => {
      if (
        destinationSelectRef.current &&
        !destinationSelectRef.current.contains(event.target as Node)
      ) {
        setDestinationOpen(false);
      }
    };

    document.addEventListener("mousedown", closeDestinationSelect);
    return () =>
      document.removeEventListener("mousedown", closeDestinationSelect);
  }, [destinationOpen]);

  const destination = destinations.find(({ id }) => id === destinationId);
  const availableFiats = useMemo(
    () => fiatsForPaymentMethod(capabilities?.fiats || [], paymentMethodId),
    [capabilities?.fiats, paymentMethodId]
  );
  const fiatOptions = useMemo(
    () => availableFiats.map((item) => ({ asset: fiatSelectOption(item) })),
    [availableFiats]
  );
  const selectedFiatAsset =
    fiatOptions.find(({ asset }) => asset.metadata.symbol === fiat)?.asset ||
    fiatOptions[0]?.asset;

  useEffect(() => {
    if (!availableFiats.length) return;
    if (availableFiats.some(({ id }) => id === fiat)) return;
    const preferred =
      availableFiats.find(
        ({ id }) =>
          id === (paymentMethodId === "sepa-bank-transfer" ? "EUR" : "USD")
      ) || availableFiats[0];
    setFiat(preferred.id);
  }, [availableFiats, fiat, paymentMethodId]);

  // What is actually sent: 0.004 USD or 0.5 JPY quantizes to zero, which is "no
  // amount", not an order to quote.
  const quantizedFiatAmount = quantizeFiat(fiatAmount, fiat);
  const fiatAmountValue = formatFiatAmount(quantizedFiatAmount);

  // The capabilities list already carries the per-payment-method minimum, so the
  // user can be told before a quote round-trip fails.
  const selectedFiat = availableFiats.find(({ id }) => id === fiat);
  const paymentMethodMinimum = selectedFiat?.supportedPaymentMethods.find(
    ({ id }) => id === paymentMethodId
  )?.minimum;
  const belowPaymentMethodMinimum =
    quantizedFiatAmount > 0n &&
    !!paymentMethodMinimum &&
    Number(fiatAmountValue) < Number(paymentMethodMinimum);
  const request = destination
    ? {
        fiat,
        crypto: destination.crypto,
        blockchain: destination.blockchain,
        paymentMethodId,
        fiatAmount: fiatAmountValue,
      }
    : undefined;

  useEffect(() => {
    setFiatAmount((current) => quantizeFiat(current, fiat));
  }, [fiat]);

  // The disclaimer is agreed against one specific order. Changing any part of it
  // means the user has not agreed to what they would now be submitting.
  useEffect(() => {
    setAccepted(false);
  }, [fiat, fiatAmountValue, paymentMethodId, destinationId]);

  useEffect(() => {
    if (!request || quantizedFiatAmount === 0n || belowPaymentMethodMinimum) {
      setQuote(undefined);
      setQuoteState("idle");
      setError("");
      setMinimumError("");
      return;
    }

    let active = true;
    setQuote(undefined);
    setQuoteState("loading");
    setError("");
    setMinimumError("");
    const timeout = window.setTimeout(() => {
      getBanxaQuote(request)
        .then((nextQuote) => {
          if (!active) return;
          setQuote(nextQuote);
          setQuoteState("ready");
        })
        .catch((reason: unknown) => {
          if (!active) return;
          if (reason instanceof BanxaMinimumAmountError) {
            setMinimumError(
              t("banxaMinimumPurchase", {
                fiat: reason.fiat,
                amount: reason.minimumFiatAmount,
              })
            );
          } else {
            setError(t("banxaQuoteError"));
          }
          setQuoteState("error");
        });
    }, 450);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [
    fiat,
    fiatAmountValue,
    paymentMethodId,
    destination?.crypto,
    destination?.blockchain,
  ]);

  const selectPaymentMethod = (method: BanxaPaymentMethod) => {
    setPaymentMethodId(method);
  };

  const submit = async () => {
    if (!request || !destination || !quote || !accepted) return;

    setSubmitting(true);
    setError("");

    try {
      const order = await createBanxaOrder({
        ...request,
        walletAddress: destination.address.address,
        customerId:
          accountProvider.selected?.address.address ||
          destination.address.address,
      });

      window.location.assign(order.checkoutUrl);
    } catch {
      setError(t("banxaOrderError"));
      setSubmitting(false);
    }
  };

  const gas = destination ? gasToken(destination.network).symbol : undefined;
  const rate = quote ? quoteRate(quote) : undefined;
  const fee = banxaFeeTotal(quote);

  return (
    <div className="banxa-ramp">
      <div className="banxa-ramp__header">
        <button
          type="button"
          className="transparent color-white hover-primary1 flex ai-c fs-14 fw-500"
          onClick={stage === "review" ? () => setStage("configure") : onBack}>
          <Icons.AngleLeft className="block w-2 h-2 mr-0.5" />
          {t("back")}
        </button>
        {__BANXA_ENVIRONMENT__ === "sandbox" && (
          <span className="banxa-ramp__environment">{t("sandbox")}</span>
        )}
      </div>

      <h3 className="h3 mb-0">{t("buyCrypto")}</h3>
      <p className="p color-grey">
        {stage === "configure" ? t("banxaIntro") : t("banxaReviewIntro")}
      </p>

      {stage === "configure" ? (
        <>
          <span className="banxa-ramp__eyebrow">{t("youPay")}</span>
          <SwapSelect
            selected={selectedFiatAsset}
            options={fiatOptions.length > 1 ? fiatOptions : undefined}
            onSelect={(asset) => setFiat(asset.metadata.symbol)}
            sortOptions={() => 0}
            amount={fiatAmount}
            onChangeAmount={setFiatAmount}
            className="swap-select--responsive swap-select--dark"
          />
          {belowPaymentMethodMinimum ? (
            <p className="banxa-ramp__minimum-error">
              {t("banxaMinimumPurchase", {
                fiat,
                amount: paymentMethodMinimum,
              })}
            </p>
          ) : (
            minimumError && (
              <p className="banxa-ramp__minimum-error">{minimumError}</p>
            )
          )}

          <span className="banxa-ramp__eyebrow mt-2">{t("youReceive")}</span>
          {capabilitiesState === "loading" ? (
            <div className="banxa-ramp__empty" role="status">
              <Icons.Info className="w-3 h-3" />
              <div>
                <strong>{t("banxaLoadingAssetsTitle")}</strong>
                <span>{t("banxaLoadingAssetsBody")}</span>
              </div>
            </div>
          ) : capabilitiesState === "error" ? (
            <div className="banxa-ramp__empty" role="alert">
              <Icons.Info className="w-3 h-3" />
              <div>
                <strong>{t("banxaAssetsUnavailableTitle")}</strong>
                <span>{t("banxaAssetsUnavailableBody")}</span>
              </div>
            </div>
          ) : destinations.length ? (
            <div className="row wrap pad--sm gap-y-1 jc-c">
              <div className="col-12 col-md-7">
                <div
                  ref={destinationSelectRef}
                  className="banxa-ramp__destination-select">
                  {destination && (
                    <TokenSelect
                      selected={productAsset(destination)}
                      network={destination.network}
                      address={destination.address.address}
                      onClick={() => setDestinationOpen((open) => !open)}
                      className={clsx("token-select--dark", {
                        "token-select--open": destinationOpen,
                      })}
                    />
                  )}
                  {destinationOpen && (
                    <div
                      className="token-select__dropdown token-select__dropdown--dark common-scrollbars"
                      role="listbox"
                      aria-label={t("assetAndWallet")}>
                      {destinations.map((option) => (
                        <a
                          key={option.id}
                          role="option"
                          aria-selected={option.id === destinationId}
                          className={clsx("banxa-ramp__destination-option", {
                            "banxa-ramp__destination-option--selected":
                              option.id === destinationId,
                          })}
                          onClick={() => {
                            setDestinationId(option.id);
                            setDestinationOpen(false);
                          }}>
                          <IconDenom denom={option.symbol} />
                          <span className="condensed">{option.symbol}</span>
                          <span className="banxa-ramp__destination-option-meta">
                            <strong>{networkLabel(option.network)}</strong>
                            <small>
                              {shortAddress(option.address.address)}
                            </small>
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-5">
                <div
                  className="swap-select swap-select--nohover swap-select--noselected swap-select--dark condensed"
                  aria-live="polite">
                  <div className="swap-select__value">
                    <span className="val">
                      {quoteState === "loading"
                        ? t("calculating")
                        : quote
                          ? formatNumber(quote.cryptoAmount)
                          : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="banxa-ramp__empty">
              <Icons.Wallet className="w-3 h-3" />
              <div>
                <strong>{t("banxaConnectWalletTitle")}</strong>
                <span>{t("banxaConnectWalletBody")}</span>
              </div>
            </div>
          )}

          <span className="banxa-ramp__eyebrow mt-2">{t("paymentMethod")}</span>
          <div className="banxa-ramp__payment-methods">
            {paymentMethods.map(({ id, label, description, Icon }) => (
              <button
                key={id}
                type="button"
                aria-pressed={paymentMethodId === id}
                className={clsx("banxa-ramp__payment-method", {
                  "banxa-ramp__payment-method--selected":
                    paymentMethodId === id,
                })}
                onClick={() => selectPaymentMethod(id)}>
                <Icon className="w-3.5 h-3.5" />
                <span>
                  <strong>{label}</strong>
                  <small>{description}</small>
                </span>
              </button>
            ))}
          </div>

          <div className="banxa-ramp__quote" aria-live="polite">
            <div>
              <span>{t("effectiveRate")}</span>
              <strong>
                {rate
                  ? `1 ${destination?.symbol} ≈ ${formatNumber(String(rate), 2)} ${fiat}`
                  : "—"}
              </strong>
            </div>

            <div>
              <span
                className="tooltip-label"
                data-tooltip-id="global-tip"
                data-tooltip-html={t("banxaEstimatedFeeTooltip")}
                tabIndex={0}>
                {t("estimatedFees")}
              </span>
              <strong>
                {formatNumber(String(fee), 2)} {fiat}
              </strong>
            </div>

            <div>
              <span
                className="tooltip-label"
                data-tooltip-id="global-tip"
                data-tooltip-html={t("banxaEstimatedTimeTooltip")}
                tabIndex={0}>
                {t("estimatedTime")}
              </span>
              <strong>{t("banxaEstimatedTime")}</strong>
            </div>
          </div>

          {error && <BanxaErrorMessage message={error} />}

          <Button
            className="button--big w-full mt-2"
            disabled={
              !quote ||
              !destination ||
              quoteState !== "ready" ||
              belowPaymentMethodMinimum
            }
            onClick={() => setStage("review")}
            label={
              quoteState === "loading" ? t("gettingQuote") : t("reviewPurchase")
            }
          />
        </>
      ) : (
        <>
          <div className="card px-2 py-1 mt-2">
            <BanxaReviewRow
              label={t("youPay")}
              value={`${formatNumber(quote?.fiatAmount || fiatAmountValue, 2)} ${fiat}`}
            />
            <BanxaReviewRow
              label={t("youReceive")}
              value={`≈ ${formatNumber(quote?.cryptoAmount || "0")} ${destination?.symbol || ""}`}
            />
            <BanxaFeeBreakdown quote={quote} fiat={fiat} />
            <BanxaReviewRow
              label={t("paymentMethod")}
              value={
                paymentMethods.find(({ id }) => id === paymentMethodId)
                  ?.label || paymentMethodId
              }
            />
            <BanxaReviewRow
              label={t("network")}
              value={destination ? networkLabel(destination.network) : ""}
            />
            <BanxaReviewRow
              label={t("walletAddress")}
              value={destination?.address.address || ""}
              mono
            />
          </div>

          <div className="card p-2 mt-2">
            <label className="banxa-ramp__provider">
              <span className="banxa-ramp__provider-title">
                <span className="banxa-ramp__wordmark" aria-label="Banxa">
                  B<span>△</span>NX<span>△</span>
                </span>
                <span>{t("serviceProvider")}</span>
              </span>
              <span className="banxa-ramp__provider-copy">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(event) => setAccepted(event.currentTarget.checked)}
                />
                <span>{t("banxaDisclaimer")}</span>
              </span>
            </label>
          </div>

          {destination && destination.network !== THOR && (
            <Warning
              color="orange"
              msg={t("banxaGasReminder", {
                asset: destination?.symbol,
                network: destination ? networkLabel(destination.network) : "",
                gas,
              })}
              className="mt-2 condensed"
            />
          )}

          <p className="banxa-ramp__indicative">{t("banxaIndicativeQuote")}</p>
          {error && <BanxaErrorMessage message={error} />}

          <div className="modal__footer banxa-ramp__footer px-3 py-2">
            <Button
              className="button--grey button--outline"
              onClick={() => setStage("configure")}
              label={t("back")}
            />
            <Button
              className={clsx("ml-1", {
                "button--waiting": submitting,
              })}
              disabled={!accepted || submitting}
              onClick={submit}
              label={submitting ? t("openingBanxa") : t("continueWithBanxa")}
            />
          </div>
        </>
      )}
    </div>
  );
};

const BanxaErrorMessage: FC<{ message: string }> = ({ message }) => (
  <div className="banxa-ramp__error" role="alert">
    <Icons.Info aria-hidden="true" />
    <span>{message}</span>
  </div>
);
